/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { Bot, Trash } from 'lucide-react';

import { Button } from '../ui/button';

import { CodeAgentChatHistory } from '@/commons/data';

interface ChatHistoryProps {
    savedChats: CodeAgentChatHistory[];
    currentChatId: string | null;
    onLoadChat: (chat: CodeAgentChatHistory) => void;
    onDeleteChat: (chatId: string) => void;
}

export const ChatHistory = ({ savedChats, currentChatId, onLoadChat, onDeleteChat }: ChatHistoryProps) => {
    const formatDateTime = (timestamp: number) => {
        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        if (date.toDateString() === today.toDateString()) {
            return `Today at ${timeString}`;
        } else if (date.toDateString() === yesterday.toDateString()) {
            return `Yesterday at ${timeString}`;
        }
        return `${date.toLocaleDateString()} at ${timeString}`;
    };

    return (
        <div className="bg-background flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-4">
                {savedChats.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                            <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No chat history yet</p>
                            <p className="text-sm mt-1">Start a conversation to see your chat history here.</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {savedChats.map((chat) => (
                            <div
                                key={chat.id}
                                className={`group p-3 rounded-lg border cursor-pointer hover:bg-muted/50
                                    transition-colors ${currentChatId === chat.id ? 'bg-muted' : ''}`}
                                onClick={() => onLoadChat(chat)}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-sm truncate">{chat.title}</h4>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {formatDateTime(chat.lastMessageDate)} • {chat.messages.length} messages
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteChat(chat.id);
                                        }}
                                        className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:opacity-100"
                                    >
                                        <Trash className="h-3 w-3" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
